// What a device may propose to the shared catalogue: POST /v1/submissions on the edge. The shape
// is the contract's own (@proef/contract), so the edge cannot refuse what this file lets through;
// `sanitise` only shapes free-typed text to it first (trim, cut to length, drop what is blank).
import { NewSubmission } from "@proef/contract";
import type { z } from "zod";

/** A submission as a screen builds it: optional facts may be left out, `sanitise` fills the rest. */
export type SubmissionDraft = z.input<typeof NewSubmission>;

const TEXT_MAX = 200;
const GRAPE_MAX = 100;
const GRAPES_MAX = 30;

/** Trimmed and cut to the edge's limit; `undefined` when nothing is left. */
function text(value: string, max = TEXT_MAX): string | undefined {
  const cut = value.trim().slice(0, max).trim();
  return cut === "" ? undefined : cut;
}

const country = (value: string | undefined): string | undefined =>
  value !== undefined && /^[A-Za-z]{2}$/.test(value) ? value.toUpperCase() : undefined;

const vintage = (value: number | null | undefined): number | undefined =>
  value !== undefined && value !== null && Number.isInteger(value) && value >= 1800 && value <= 2100
    ? value
    : undefined;

/**
 * The submission as the edge will accept it, or `undefined` when it has nothing to say (an empty
 * name, a GTIN that is not 8 to 14 digits). Unknown or empty optionals are left out, not sent blank.
 */
export function sanitise(draft: SubmissionDraft): NewSubmission | undefined {
  const shaped = shape(draft);
  if (shaped === undefined) return undefined;
  // The contract has the last word, so a shape the edge would refuse is never queued.
  const parsed = NewSubmission.safeParse(shaped);
  return parsed.success ? parsed.data : undefined;
}

function shape(submission: SubmissionDraft): SubmissionDraft | undefined {
  if (submission.kind === "producer") {
    const name = text(submission.name);
    const cc = country(submission.country);
    return name === undefined
      ? undefined
      : { kind: "producer", name, ...(cc ? { country: cc } : {}) };
  }
  if (submission.kind === "wine") {
    const producer = text(submission.producer);
    const name = text(submission.name);
    if (producer === undefined || name === undefined) return undefined;
    const cc = country(submission.country);
    const year = vintage(submission.vintage);
    const grapes = (submission.grapes ?? [])
      .map((g) => text(g, GRAPE_MAX))
      .filter((g) => g !== undefined)
      .slice(0, GRAPES_MAX);
    const manualWineId = text(submission.manualWineId ?? "");
    return {
      kind: "wine",
      producer,
      name,
      ...(manualWineId === undefined ? {} : { manualWineId }),
      ...(year === undefined ? {} : { vintage: year }),
      ...(cc === undefined ? {} : { country: cc }),
      ...(submission.colour === undefined ? {} : { colour: submission.colour }),
      sparkling: submission.sparkling === true,
      grapes,
    };
  }
  const producer = text(submission.wine.producer);
  const name = text(submission.wine.name);
  if (producer === undefined || name === undefined || !/^[0-9]{8,14}$/.test(submission.gtin))
    return undefined;
  const year = vintage(submission.wine.vintage);
  return {
    kind: "barcode",
    gtin: submission.gtin,
    wine: { producer, name, ...(year === undefined ? {} : { vintage: year }) },
    ...(submission.catalogueWineId === undefined
      ? {}
      : { catalogueWineId: submission.catalogueWineId.slice(0, TEXT_MAX) }),
  };
}
